import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaUsers, FaHeart, FaCrown, FaChartLine } from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

const AdminReports = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0, maleUsers: 0, femaleUsers: 0,
    verifiedUsers: 0, activeSubscriptions: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/api/admin/dashboard');
        if (res.data.success) setStats(res.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const genderData = {
    labels: ['Male', 'Female'],
    datasets: [{
      data: [stats.maleUsers || 0, stats.femaleUsers || 0],
      backgroundColor: ['#3B82F6', '#EC4899'],
      borderWidth: 0
    }]
  };

  const monthlyData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'New Registrations',
      data: [12, 19, 15, 25, 22, 30],
      borderColor: '#8B0000',
      backgroundColor: 'rgba(139,0,0,0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const subscriptionData = {
    labels: ['Basic', 'Premium', 'Royal', 'Free'],
    datasets: [{
      label: 'Users',
      data: [stats.activeSubscriptions || 5, 8, 3, stats.totalUsers ? stats.totalUsers - 16 : 20],
      backgroundColor: ['#B8860B', '#FFD700', '#8B0000', '#D1D5DB']
    }]
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#8B0000] border-t-transparent"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#FFF8F0] pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/admin" className="text-[#8B0000] hover:underline flex items-center gap-1 text-sm mb-2">
            <FaArrowLeft /> Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-[#8B0000] font-['Playfair_Display'] mb-1">Reports & Analytics</h1>
          <p className="text-gray-600 font-['Noto_Sans_Devanagari'] mb-8">अहवाल आणि विश्लेषण</p>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { icon: FaUsers, label: 'Total Users', value: stats.totalUsers, color: '#8B0000' },
              { icon: FaHeart, label: 'Matches Made', value: stats.totalMatches || 0, color: '#DC143C' },
              { icon: FaCrown, label: 'Premium Users', value: stats.activeSubscriptions || 0, color: '#B8860B' },
              { icon: FaChartLine, label: 'Verified', value: stats.verifiedUsers || 0, color: '#16a34a' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md p-4 text-center">
                <item.icon className="text-2xl mx-auto mb-2" style={{ color: item.color }} />
                <p className="text-2xl font-bold" style={{ color: item.color }}>{item.value}</p>
                <p className="text-sm text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-[#8B0000] mb-4">Gender Distribution</h3>
              <div className="max-w-[250px] mx-auto">
                <Doughnut data={genderData} options={{ plugins: { legend: { position: 'bottom' } } }} />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-[#8B0000] mb-4">Registration Trend</h3>
              <Line data={monthlyData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-[#8B0000] mb-4">Subscription Distribution</h3>
            <Bar data={subscriptionData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminReports;
